
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {UpdateType} from './UpdateType';
import {FieldModelUpdateType} from './UpdateType';
import {QuoteType} from './QuoteType';
import {FieldModelQuoteType} from './QuoteType';
import {Quote} from './Quote';
import {FieldModelQuote} from './Quote';

/**
 * TickClientUpdate struct
 */
class TickClientUpdate {
  /**
   * Initialize struct
   * @param {!string=} symbol
   * @param {!Date=} time
   * @param {!UpdateType=} updateType
   * @param {!QuoteType=} quoteType
   * @param {Quote=} quote
   * @param {!number=} priceChangePercent
   * @param {!number=} priceChange
   * @constructor
   */
  constructor (symbol = '', time = new Date(0), updateType = new UpdateType(), quoteType = new QuoteType(), quote = null, priceChangePercent = 0.0, priceChange = 0.0) {
    this.Symbol = symbol
    this.Time = time
    this.UpdateType = updateType
    this.QuoteType = quoteType
    this.Quote = quote
    this.PriceChangePercent = priceChangePercent
    this.PriceChange = priceChange
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TickClientUpdate}
   * @param {!TickClientUpdate} other Other struct
   * @returns {!TickClientUpdate} This struct
   */
  copy (other) {
    if (other.Symbol != null) {
      this.Symbol = other.Symbol
    } else {
      this.Symbol = null
    }
    if (other.Time != null) {
      if (other.Time instanceof Date) {
        this.Time = new Date(other.Time.getTime())
      } else {
        this.Time = new Date(Math.round(other.Time / 1000000))
      }
    } else {
      this.Time = null
    }
    if (other.UpdateType != null) {
      this.UpdateType = UpdateType.fromObject(other.UpdateType)
    } else {
      this.UpdateType = null
    }
    if (other.QuoteType != null) {
      this.QuoteType = QuoteType.fromObject(other.QuoteType)
    } else {
      this.QuoteType = null
    }
    if (other.Quote != null) {
      this.Quote = Quote.fromObject(other.Quote)
    } else {
      this.Quote = null
    }
    if (other.PriceChangePercent != null) {
      this.PriceChangePercent = other.PriceChangePercent
    } else {
      this.PriceChangePercent = null
    }
    if (other.PriceChange != null) {
      this.PriceChange = other.PriceChange
    } else {
      this.PriceChange = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TickClientUpdate}
   * @returns {!TickClientUpdate} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TickClientUpdateModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TickClientUpdateModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TickClientUpdate}
   * @param {!TickClientUpdate} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TickClientUpdate)) {
      throw new TypeError('Instance of TickClientUpdate is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TickClientUpdate}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Symbol: ((this.Symbol != null) ? this.Symbol : null),
      Time: ((this.Time != null) ? (this.Time.getTime() * 1000000) : null),
      UpdateType: ((this.UpdateType != null) ? this.UpdateType : null),
      QuoteType: ((this.QuoteType != null) ? this.QuoteType : null),
      Quote: ((this.Quote != null) ? this.Quote : null),
      PriceChangePercent: ((this.PriceChangePercent != null) ? this.PriceChangePercent : null),
      PriceChange: ((this.PriceChange != null) ? this.PriceChange : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TickClientUpdate.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TickClientUpdate} other Object value
   * @returns {!TickClientUpdate} Created struct
   */
  static fromObject (other) {
    return new TickClientUpdate().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TickClientUpdate}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TickClientUpdate.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TickClientUpdate}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 34
  }
}

export { TickClientUpdate };

/**
 * Fast Binary Encoding TickClientUpdate field model
 */
class FieldModelTickClientUpdate extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Symbol = new fbe.FieldModelString(buffer, 4 + 4)
    this.Time = new fbe.FieldModelTimestamp(buffer, this.Symbol.fbeOffset + this.Symbol.fbeSize)
    this.UpdateType = new FieldModelUpdateType(buffer, this.Time.fbeOffset + this.Time.fbeSize)
    this.QuoteType = new FieldModelQuoteType(buffer, this.UpdateType.fbeOffset + this.UpdateType.fbeSize)
    this.Quote = new fbe.FieldModelOptional(new FieldModelQuote(buffer, this.QuoteType.fbeOffset + this.QuoteType.fbeSize), buffer, this.QuoteType.fbeOffset + this.QuoteType.fbeSize)
    this.PriceChangePercent = new fbe.FieldModelDouble(buffer, this.Quote.fbeOffset + this.Quote.fbeSize)
    this.PriceChange = new fbe.FieldModelDouble(buffer, this.PriceChangePercent.fbeOffset + this.PriceChangePercent.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTickClientUpdate}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTickClientUpdate}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Symbol.fbeSize + this.Time.fbeSize + this.UpdateType.fbeSize + this.QuoteType.fbeSize + this.Quote.fbeSize + this.PriceChangePercent.fbeSize + this.PriceChange.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTickClientUpdate}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Symbol.fbeExtra + this.Time.fbeExtra + this.UpdateType.fbeExtra + this.QuoteType.fbeExtra + this.Quote.fbeExtra + this.PriceChangePercent.fbeExtra + this.PriceChange.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTickClientUpdate}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTickClientUpdate.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTickClientUpdate}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 34
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTickClientUpdate}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTickClientUpdate}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Symbol.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Symbol.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Symbol.fbeSize

    if ((fbeCurrentSize + this.Time.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Time.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.UpdateType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UpdateType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize

    if ((fbeCurrentSize + this.QuoteType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.QuoteType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.QuoteType.fbeSize

    if ((fbeCurrentSize + this.Quote.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Quote.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Quote.fbeSize

    if ((fbeCurrentSize + this.PriceChangePercent.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PriceChangePercent.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PriceChangePercent.fbeSize

    if ((fbeCurrentSize + this.PriceChange.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PriceChange.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PriceChange.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTickClientUpdate}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTickClientUpdate}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTickClientUpdate}
   * @param {!TickClientUpdate} fbeValue Default value, defaults is new TickClientUpdate()
   * @returns {!TickClientUpdate} TickClientUpdate value
   */
  get (fbeValue = new TickClientUpdate()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTickClientUpdate}
   * @param {!TickClientUpdate} fbeValue TickClientUpdate value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Symbol.fbeSize) <= fbeStructSize) {
      fbeValue.Symbol = this.Symbol.get()
    } else {
      fbeValue.Symbol = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Symbol.fbeSize

    if ((fbeCurrentSize + this.Time.fbeSize) <= fbeStructSize) {
      fbeValue.Time = this.Time.get()
    } else {
      fbeValue.Time = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.UpdateType.fbeSize) <= fbeStructSize) {
      fbeValue.UpdateType = this.UpdateType.get()
    } else {
      fbeValue.UpdateType = new UpdateType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize

    if ((fbeCurrentSize + this.QuoteType.fbeSize) <= fbeStructSize) {
      fbeValue.QuoteType = this.QuoteType.get()
    } else {
      fbeValue.QuoteType = new QuoteType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.QuoteType.fbeSize

    if ((fbeCurrentSize + this.Quote.fbeSize) <= fbeStructSize) {
      fbeValue.Quote = this.Quote.get()
    } else {
      fbeValue.Quote = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Quote.fbeSize

    if ((fbeCurrentSize + this.PriceChangePercent.fbeSize) <= fbeStructSize) {
      fbeValue.PriceChangePercent = this.PriceChangePercent.get()
    } else {
      fbeValue.PriceChangePercent = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PriceChangePercent.fbeSize

    if ((fbeCurrentSize + this.PriceChange.fbeSize) <= fbeStructSize) {
      fbeValue.PriceChange = this.PriceChange.get()
    } else {
      fbeValue.PriceChange = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PriceChange.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTickClientUpdate}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTickClientUpdate}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTickClientUpdate}
   * @param {!TickClientUpdate} fbeValue TickClientUpdate value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTickClientUpdate}
   * @param {TickClientUpdate} fbeValue TickClientUpdate value
   */
  setFields (fbeValue) {
    this.Symbol.set(fbeValue.Symbol)
    this.Time.set(fbeValue.Time)
    this.UpdateType.set(fbeValue.UpdateType)
    this.QuoteType.set(fbeValue.QuoteType)
    this.Quote.set(fbeValue.Quote)
    this.PriceChangePercent.set(fbeValue.PriceChangePercent)
    this.PriceChange.set(fbeValue.PriceChange)
  }
}

export { FieldModelTickClientUpdate };

/**
 * Fast Binary Encoding TickClientUpdate model
 */
class TickClientUpdateModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTickClientUpdate(this.buffer, 4)
  }

  /**
   * Get the TickClientUpdate model
   * @this {!TickClientUpdateModel}
   * @returns {!FieldModelTickClientUpdate} model TickClientUpdate model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TickClientUpdateModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TickClientUpdateModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TickClientUpdateModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TickClientUpdateModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTickClientUpdate.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TickClientUpdateModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TickClientUpdateModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TickClientUpdateModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TickClientUpdateModel}
   * @param {!TickClientUpdate} value TickClientUpdate value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TickClientUpdateModel}
   * @param {!TickClientUpdate} value TickClientUpdate value, defaults is new TickClientUpdate()
   * @return {!object} Deserialized TickClientUpdate value and its size
   */
  deserialize (value = new TickClientUpdate()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TickClientUpdate(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TickClientUpdate(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TickClientUpdateModel}
   * @param {!number} prev Previous TickClientUpdate model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TickClientUpdateModel };
